/**
 * Structured error helpers for the statistics API.
 *
 * Statistics services read the axios error directly and bypass
 * `AxiosManager.stringifyError` (`src/app/services/axiosManager/index.js`),
 * which throws away the `statusCode` and `details.field` the gateway sends
 * (`{ status: false, statusCode, error, details: { field }, time }`).
 */

/**
 * `{ status, message, field }` from a raw axios error.
 * - `status`: `response.status`, else `response.data.statusCode`, else
 *   `null` for a network error (no `response`).
 * - `message`: `response.data.error`, else `err.message`.
 * - `field`: `response.data.details.field`, else `null`.
 */
export const toStatsError = (axiosErr) => {
  const response = axiosErr?.response;
  const status = response?.status ?? response?.data?.statusCode ?? null;
  const message = response?.data?.error ?? axiosErr?.message ?? null;
  const field = response?.data?.details?.field ?? null;

  return { status, message, field };
};

export const STATS_ERROR_KEY = {
  BUSY: 'STATS_ERROR_BUSY',
  INVALID: 'STATS_ERROR_INVALID',
  FORBIDDEN: 'STATS_ERROR_FORBIDDEN',
  GENERIC: 'STATS_ERROR_GENERIC',
};

/**
 * `{ key, retry }` for a `toStatsError` result: a 503 is busy and retryable,
 * a 400 is an invalid request shown with its field and is not retried, a 403
 * is forbidden and is not retried, and anything else is a generic, retryable
 * error.
 */
export const describeStatsError = (err) => {
  if (err?.status === 503) {
    return { key: STATS_ERROR_KEY.BUSY, retry: true };
  }
  if (err?.status === 400) {
    return { key: STATS_ERROR_KEY.INVALID, retry: false };
  }
  if (err?.status === 403) {
    return { key: STATS_ERROR_KEY.FORBIDDEN, retry: false };
  }

  return { key: STATS_ERROR_KEY.GENERIC, retry: true };
};
